import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Download, FileText, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Company {
  id: string;
  name: string;
}

interface CSVImportProps {
  company: Company | null;
  onImportComplete: () => void;
}

interface CSVEmployee {
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  residenceCountry: string;
  nestPoints: number;
  seniorityLevel?: string;
  managerEmail?: string;
}

export const CSVImport = ({ company, onImportComplete }: CSVImportProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [csvData, setCsvData] = useState<CSVEmployee[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const downloadTemplate = () => {
    const headers = ['firstName', 'lastName', 'email', 'position', 'residenceCountry', 'nestPoints', 'seniorityLevel', 'managerEmail'];
    const template = [
      headers.join(','),
      'Jean,Dupont,jean.dupont@exemple.com,Développeur,France,10,L3,manager@exemple.com',
      'Marie,Martin,marie.martin@exemple.com,Designer,Espagne,15,L4,manager@exemple.com',
      'Pierre,Durand,pierre.durand@exemple.com,Manager,Portugal,20,L5,'
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_employes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (text: string): CSVEmployee[] => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    
    const employees: CSVEmployee[] = [];
    const newErrors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
      
      if (values.length !== headers.length) {
        newErrors.push(`Ligne ${i + 1}: Nombre de colonnes incorrect`);
        continue;
      }

      const employee: any = {};
      headers.forEach((header, index) => {
        employee[header] = values[index];
      });

      // Validation
      if (!employee.firstName || !employee.lastName || !employee.email) {
        newErrors.push(`Ligne ${i + 1}: Nom, prénom et email sont obligatoires`);
        continue;
      }

      if (!employee.email.includes('@')) {
        newErrors.push(`Ligne ${i + 1}: Email invalide`);
        continue;
      }

      const nestPoints = parseInt(employee.nestPoints) || 10;
      if (nestPoints < 0) {
        newErrors.push(`Ligne ${i + 1}: Les Nest Points doivent être positifs`);
        continue;
      }

      employees.push({
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        position: employee.position || '',
        residenceCountry: employee.residenceCountry || '',
        nestPoints,
        seniorityLevel: employee.seniorityLevel || '',
        managerEmail: employee.managerEmail || ''
      });
    }

    setErrors(newErrors);
    return employees;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez sélectionner un fichier CSV"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const employees = parseCSV(text);
      setCsvData(employees);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!company || csvData.length === 0) return;

    setIsProcessing(true);
    const successCount = [];
    const failureCount = [];

    try {
      // Pre-fetch all employees in the company to lookup managers by email
      const { data: companyEmployees } = await supabase
        .from('employees')
        .select('id, email')
        .eq('company_id', company.id);
      
      const employeesByEmail = new Map<string, string>();
      (companyEmployees || []).forEach(e => {
        if (e.email) employeesByEmail.set(e.email.toLowerCase(), e.id);
      });

      // First pass: Import all employees without manager assignments
      const importedEmployees: { email: string; id: string; managerEmail?: string }[] = [];

      for (const employee of csvData) {
        try {
          // Check if employee already exists
          const { data: existingEmployee } = await supabase
            .from('employees')
            .select('id')
            .eq('company_id', company.id)
            .eq('email', employee.email)
            .single();

          if (existingEmployee) {
            failureCount.push(`${employee.firstName} ${employee.lastName}: Email déjà utilisé`);
            continue;
          }

          // Insert employee without manager (will assign in second pass)
          const { data: employeeData, error: employeeError } = await supabase
            .from('employees')
            .insert({
              company_id: company.id,
              first_name: employee.firstName,
              last_name: employee.lastName,
              email: employee.email,
              position: employee.position || null,
              residence_country: employee.residenceCountry || null,
              invitation_status: 'invitation_non_envoyee'
            })
            .select('id')
            .single();

          if (employeeError) throw employeeError;

          // Create employee points record
          const now = new Date();
          const { error: pointsError } = await supabase
            .from('employee_points')
            .insert({
              employee_id: employeeData.id,
              month: now.getMonth() + 1,
              year: now.getFullYear(),
              total_points: employee.nestPoints,
              used_points: 0
            });

          if (pointsError) throw pointsError;

          // Track for second pass
          importedEmployees.push({
            email: employee.email.toLowerCase(),
            id: employeeData.id,
            managerEmail: employee.managerEmail
          });
          employeesByEmail.set(employee.email.toLowerCase(), employeeData.id);

          successCount.push(`${employee.firstName} ${employee.lastName}`);
        } catch (error: any) {
          failureCount.push(`${employee.firstName} ${employee.lastName}: ${error.message}`);
        }
      }

      // Second pass: Assign managers now that all employees exist
      for (const imported of importedEmployees) {
        if (imported.managerEmail) {
          const managerId = employeesByEmail.get(imported.managerEmail.toLowerCase());
          if (managerId) {
            await supabase
              .from('employees')
              .update({ manager_id: managerId })
              .eq('id', imported.id);
          } else {
            console.warn(`Manager email not found: ${imported.managerEmail}`);
          }
        }
      }

      toast({
        title: "Import terminé",
        description: `${successCount.length} employé(s) importé(s) avec succès${failureCount.length > 0 ? `, ${failureCount.length} échec(s)` : ''}`
      });

      if (successCount.length > 0) {
        setIsDialogOpen(false);
        setCsvData([]);
        setErrors([]);
        onImportComplete();
      }

      if (failureCount.length > 0) {
        setErrors(failureCount);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Erreur lors de l'import: " + error.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Importer CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importer des employés par CSV</DialogTitle>
          <DialogDescription>
            Importez plusieurs employés en une fois à partir d'un fichier CSV
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Template CSV</p>
                <p className="text-sm text-muted-foreground">
                  Téléchargez le modèle avec les colonnes requises
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Télécharger
            </Button>
          </div>

          {/* File Upload */}
          <div>
            <Label htmlFor="csvFile">Fichier CSV</Label>
            <Input
              ref={fileInputRef}
              id="csvFile"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Colonnes: firstName, lastName, email, position, residenceCountry, nestPoints, seniorityLevel, managerEmail (optionnels)
            </p>
          </div>

          {/* Preview */}
          {csvData.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Aperçu ({csvData.length} employé(s))</h4>
              <div className="max-h-32 overflow-y-auto border rounded p-2 text-sm">
                {csvData.slice(0, 5).map((employee, index) => (
                  <div key={index} className="py-1">
                    {employee.firstName} {employee.lastName} - {employee.email}
                  </div>
                ))}
                {csvData.length > 5 && (
                  <div className="text-muted-foreground">
                    ... et {csvData.length - 5} autre(s)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">Erreurs détectées:</p>
                  <div className="max-h-32 overflow-y-auto">
                    {errors.map((error, index) => (
                      <div key={index} className="text-sm">• {error}</div>
                    ))}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleImport}
            disabled={csvData.length === 0 || errors.length > 0 || isProcessing}
          >
            {isProcessing ? "Import en cours..." : `Importer ${csvData.length} employé(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};